// First Frame Generation Modal

import React, { useState, useEffect } from 'react';
import { imagesApi } from '../../services/api';
import type { FirstFrameSettings } from '../../types';

interface FirstFrameModalProps {
  isOpen: boolean;
  projectId: string;
  sceneNumber: number;
  sceneData?: {
    first_frame_description?: string;
    environment_prompt?: string;
    character_key?: string;
    character?: any;
  };
  onClose: () => void;
  onTaskCreated: (taskId: string) => void;
}

const FirstFrameModal: React.FC<FirstFrameModalProps> = ({
  isOpen,
  projectId,
  sceneNumber,
  sceneData,
  onClose,
  onTaskCreated,
}) => {
  const [model, setModel] = useState<FirstFrameSettings['model']>('seedream');
  const [colorGrading, setColorGrading] = useState('natural');
  const [generating, setGenerating] = useState(false);

  // Сбросить состояние generating при открытии модала
  useEffect(() => {
    if (isOpen) {
      setGenerating(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    setGenerating(true);

    try {
      const result = await imagesApi.generateFirstFrame({
        project_id: parseInt(projectId),
        scene_number: sceneNumber,
        first_frame_description: sceneData?.first_frame_description || '',
        environment_prompt: sceneData?.environment_prompt || '',
        character_key: sceneData?.character_key,
        character_reference_prompt: sceneData?.character?.reference_prompt,
        model,
        color_grading: colorGrading,
      });

      if (result.success && result.task_id) {
        onTaskCreated(result.task_id);
        onClose();
      } else {
        alert('Failed to start first frame generation');
        setGenerating(false);
      }
    } catch (error: any) {
      console.error('Error generating first frame:', error);
      alert('Error: ' + error.message);
      setGenerating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Generate First Frame</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="form-group">
          <label>Select Model:</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
            {(['seedream', 'nano-banana', 'nano-banana-pro'] as const).map((m) => {
              const modelInfo = {
                seedream: { name: 'SeedDream v4', desc: 'Bytedance SeedDream 4' },
                'nano-banana': { name: 'Nano Banana', desc: 'Gemini 2.5 Flash Image' },
                'nano-banana-pro': { name: 'Nano Banana Pro', desc: 'Gemini 3 Pro Image' },
              };

              return (
                <div
                  key={m}
                  className={`model-card ${model === m ? 'selected' : ''}`}
                  onClick={() => setModel(m)}
                  style={{
                    padding: '15px',
                    border: model === m ? '2px solid #007bff' : '2px solid #dee2e6',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    background: model === m ? '#e3f2fd' : 'white',
                  }}
                >
                  <h5>{modelInfo[m].name}</h5>
                  <p style={{ fontSize: '12px', color: '#666', margin: '5px 0 0 0' }}>
                    {modelInfo[m].desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="form-group">
          <label>Color Grading Style:</label>
          <select value={colorGrading} onChange={(e) => setColorGrading(e.target.value)}>
            <option value="none">None</option>
            <option value="natural">Natural (Film-grade contrast, accurate skin tones)</option>
          </select>
        </div>

        <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '15px', margin: '15px 0' }}>
          <p style={{ margin: 0 }}><strong>This will compose:</strong></p>
          <ul style={{ listStyle: 'none', paddingLeft: 0, margin: '10px 0 0 0' }}>
            <li>✓ Character reference image</li>
            <li>✓ Environment reference image</li>
            <li>✓ Character pose and expression</li>
            <li>✓ Professional color grading</li>
          </ul>
          <p style={{ color: '#6c757d', fontSize: '13px', fontStyle: 'italic', margin: '10px 0 0 0' }}>
            The generated first frame will be automatically used for image-to-video generation.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button onClick={onClose} className="btn btn-secondary" disabled={generating}>
            Cancel
          </button>
          <button onClick={handleGenerate} className="btn btn-primary" disabled={generating}>
            {generating ? 'Generating...' : 'Generate First Frame'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstFrameModal;
